import React, { useEffect, useState } from 'react';
import axios from 'axios';

function CandidatesList({ role, filiale }) {
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [jobs, setJobs] = useState([]);

  // 🔍 Filtres
  const [filterJob, setFilterJob] = useState('');
  const [filterStage, setFilterStage] = useState('');

  // 📧 Email modal
  const [emailModal, setEmailModal] = useState({ show: false, candidateId: null, newStage: '', email: '' });
  const [emailBody, setEmailBody] = useState('');

  // 🎯 Étapes possibles dans le processus
  const stages = ['lead', 'screening', 'test', 'interview', 'offer', 'hired', 'rejected'];

  // Get forward-only stages based on current stage
  const getAvailableStages = (currentStage) => {
    const currentIndex = stages.indexOf(currentStage || 'lead');
    return stages.slice(currentIndex + 1); // Only forward stages (exclude current)
  };

  // 🎨 Renvoie une couleur spécifique pour chaque étape
  const getStageColor = (stage) => {
    const colors = {
      lead: '#6c757d',        // gris
      screening: '#17a2b8',   // bleu clair
      test: '#ffc107',        // jaune
      interview: '#007bff',   // bleu
      offer: '#28a745',       // vert
      hired: '#198754',       // vert foncé
      rejected: '#dc3545'     // rouge
    };
    return colors[stage] || '#6c757d';
  };

  // 🔁 Chargement initial des candidats
  useEffect(() => {
    const fetchCandidates = async () => {
      try {
        // 🔁 Récupération de tous les candidats
        const res = await axios.get(`${process.env.REACT_APP_DOMAINE}/api/candidates`);
        const allCandidates = res.data;

        const isGlobalAdmin = ['admin', 'ceo'].includes(role);

        // ✅ Cas 1 : si une filiale est définie → filtrage même pour admin général
        if (filiale) {
          const filtered = allCandidates.filter(c => c.job_filiale === filiale);
          setCandidates(filtered);
        }
        // ✅ Cas 2 : admin général sans filiale sélectionnée → on affiche tout
        else if (isGlobalAdmin) {
          setCandidates(allCandidates);
        }
        // ✅ Cas 3 : fallback manager ou sécurité
        else {
          const filtered = allCandidates.filter(c => c.job_filiale === filiale);
          setCandidates(filtered);
        }
      } catch (err) {
        console.error("Erreur récupération candidats :", err.message);
      }
    };

    // 🔁 Exécuter à chaque changement de rôle ou de filiale
    fetchCandidates();
  }, [role, filiale]);

  // 🔁 Chargement des offres pour le filtre
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_DOMAINE}/api/jobs`);
        const allJobs = res.data;

        const isGlobalAdmin = ['admin', 'ceo'].includes(role);

        if (filiale) {
          const filtered = allJobs.filter(job => job.filiale === filiale);
          setJobs(filtered);
        } else if (isGlobalAdmin) {
          setJobs(allJobs);
        } else {
          const filtered = allJobs.filter(job => job.filiale === filiale);
          setJobs(filtered);
        }
      } catch (err) {
        console.error("Erreur récupération offres :", err.message);
      }
    };

    fetchJobs();
  }, [role, filiale]);

  // 🔍 Filtrage des candidats affichés
  const filteredCandidates = candidates.filter((c) => {
    const matchesJob = !filterJob || c.job_id === parseInt(filterJob);
    const matchesStage = !filterStage || c.stage === filterStage;
    return matchesJob && matchesStage;
  });


  // 📧 Email templates by stage
  const getEmailTemplate = (stage, candidateName) => {
    const templates = {
      lead: `Bonjour ${candidateName},\n\nNous avons bien reçu votre candidature.\n\nCordialement,\nL'équipe RH`,
      screening: `Bonjour ${candidateName},\n\nVotre candidature est en cours d'examen.\n\nCordialement,\nL'équipe RH`,
      test: `Bonjour ${candidateName},\n\nNous vous invitons à passer les tests de sélection.\n\nCordialement,\nL'équipe RH`,
      interview: `Bonjour ${candidateName},\n\nNous souhaitons vous rencontrer en entretien.\n\nCordialement,\nL'équipe RH`,
      offer: `Bonjour ${candidateName},\n\nNous avons le plaisir de vous faire une offre.\n\nCordialement,\nL'équipe RH`,
      hired: `Bonjour ${candidateName},\n\nFélicitations ! Vous êtes embauché(e).\n\nCordialement,\nL'équipe RH`,
      rejected: `Bonjour ${candidateName},\n\nMalheureusement, nous ne pouvons pas donner suite à votre candidature.\n\nCordialement,\nL'équipe RH`
    };
    return templates[stage] || '';
  };

  // 🌀 Mise à jour d'étape avec popup email
  const handleStageChange = (candidateId, newStage) => {
    if (!newStage) return; // Don't trigger if empty option selected
    const candidate = candidates.find(c => c.id === candidateId);
    const template = getEmailTemplate(newStage, candidate.full_name);
    setEmailBody(template);
    setEmailModal({ show: true, candidateId, newStage, email: candidate.email });
  };

  const confirmStageChange = async () => {
    try {
      await axios.put(`${process.env.REACT_APP_DOMAINE}/api/candidates/${emailModal.candidateId}`, {
        stage: emailModal.newStage,
        emailBody: emailBody
      });
      setCandidates((prev) =>
        prev.map((c) => (c.id === emailModal.candidateId ? { ...c, stage: emailModal.newStage } : c))
      );
      setEmailModal({ show: false, candidateId: null, newStage: '', email: '' });
      alert('Étape mise à jour et email envoyé');
    } catch (err) {
      console.error("Erreur mise à jour étape :", err.message);
      alert('Erreur lors de la mise à jour');
    }
  };

  const cancelStageChange = () => {
    setEmailModal({ show: false, candidateId: null, newStage: '', email: '' });
  };

  // ❌ Suppression d'un candidat
  const handleDeleteCandidate = async (id) => {
    const candidate = candidates.find(c => c.id === id);
    const confirmMsg = `⚠️ Voulez-vous vraiment supprimer le candidat "${candidate?.full_name}" ?\n\nCette action est irréversible et supprimera :\n- Toutes les informations du candidat\n- Les fichiers associés (CV, lettre de motivation)\n- L'historique de candidature`;

    if (!window.confirm(confirmMsg)) return;

    try {
      await axios.delete(`${process.env.REACT_APP_DOMAINE}/api/candidates/${id}`);
      setCandidates((prev) => prev.filter((c) => c.id !== id));
      alert(`✅ Le candidat "${candidate?.full_name}" a été supprimé avec succès.`);
    } catch (err) {
      console.error("Erreur suppression candidat :", err.message);
      alert(`❌ Erreur lors de la suppression : ${err.response?.data?.error || err.message}`);
    }
  };

  // ✅ Accepter un candidat (passer à "hired")
  const handleAcceptCandidate = (id) => {
    const candidate = candidates.find(c => c.id === id);
    const template = getEmailTemplate('hired', candidate.full_name);
    setEmailBody(template);
    setEmailModal({ show: true, candidateId: id, newStage: 'hired', email: candidate.email });
    setSelectedCandidate(null);
  };

  // ❌ Refuser un candidat (passer à "rejected")
  const handleRejectCandidate = (id) => {
    const candidate = candidates.find(c => c.id === id);
    const template = getEmailTemplate('rejected', candidate.full_name);
    setEmailBody(template);
    setEmailModal({ show: true, candidateId: id, newStage: 'rejected', email: candidate.email });
    setSelectedCandidate(null);
  };

  return (
    <div className="mt-4">
      <h4>📋 Liste des candidats</h4>

      {/* 🔍 Filtres */}
      <div className="row mb-3">
        <div className="col-md-6">
          <label className="form-label">Filtrer par offre</label>
          <select
            className="form-select"
            value={filterJob}
            onChange={(e) => setFilterJob(e.target.value)}
          >
            <option value="">-- Toutes les offres --</option>
            {jobs.map((job) => (
              <option key={job.id} value={job.id}>
                {job.title} ({job.filiale})
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-6">
          <label className="form-label">Filtrer par étape</label>
          <select
            className="form-select"
            value={filterStage}
            onChange={(e) => setFilterStage(e.target.value)}
          >
            <option value="">-- Toutes les étapes --</option>
            {stages.map((stage) => (
              <option key={stage} value={stage}>
                {stage.charAt(0).toUpperCase() + stage.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {filteredCandidates.length === 0 ? (
        <p>Aucun candidat trouvé avec ces filtres.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Email</th>
                <th>Ville</th>
                <th>Offre</th>
                <th>Filiale</th>
                <th>Étape</th>
                <th>Changer l'étape</th>
                <th>CV</th>
                <th>Lettre</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCandidates.map((c) => (
                <tr key={c.id}>
                  <td data-label="Nom">{c.full_name}</td>
                  <td data-label="Email">{c.email}</td>
                  <td data-label="Ville">{c.city}</td>
                  <td data-label="Offre">{c.job_title}</td>
                  <td data-label="Filiale">{c.job_filiale}</td>

                  {/* ✅ Affichage étape avec pastille colorée */}
                  <td data-label="Étape">
                    <span
                      className="stage-dot"
                      style={{
                        backgroundColor: getStageColor(c.stage || 'lead'),
                        display: 'inline-block',
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        verticalAlign: 'middle'
                      }}
                    ></span>
                    <span className="ms-2 text-capitalize">{c.stage || 'lead'}</span>
                  </td>

                  {/* 🔁 Sélection d'étape */}
                  <td data-label="Changer l'étape">
                    <select
                      className="form-select form-select-sm"
                      value=""
                      onChange={(e) => handleStageChange(c.id, e.target.value)}
                    >
                      <option value="">-- Choisir nouvelle étape --</option>
                      {getAvailableStages(c.stage).map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </td>

                  {/* 📎 Fichiers */}
                  <td data-label="CV">
                    {c.cv_file ? (
                      <a
                        href={`${process.env.REACT_APP_DOMAINE}/uploads/${encodeURIComponent(c.cv_file)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        📄 CV
                      </a>
                    ) : '—'}
                  </td>
                  <td data-label="Lettre">
                    {c.cover_letter_file ? (
                      <a
                        href={`${process.env.REACT_APP_DOMAINE}/uploads/${encodeURIComponent(c.cover_letter_file)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        📎 Lettre
                      </a>
                    ) : '—'}
                  </td>

                  {/* ⚙️ Actions */}
                  <td data-label="Actions">
                    <button className="btn btn-sm btn-outline-info me-2" onClick={() => setSelectedCandidate(c)}>
                      Voir plus
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteCandidate(c.id)}>
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 🪟 Modale détails candidat */}
      {selectedCandidate && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Détails du candidat</h5>
                <button type="button" className="btn-close" onClick={() => setSelectedCandidate(null)}></button>
              </div>
              <div className="modal-body">
                <p><strong>Nom :</strong> {selectedCandidate.full_name}</p>
                <p><strong>Email :</strong> {selectedCandidate.email}</p>
                <p><strong>Téléphone :</strong> {selectedCandidate.phone}</p>
                <p><strong>Ville :</strong> {selectedCandidate.city}</p>
                <p><strong>Pays :</strong> {selectedCandidate.country}</p>
                <p><strong>Source :</strong> {selectedCandidate.source}</p>
                <p><strong>Offre :</strong> {selectedCandidate.job_title}</p>
                <p><strong>Filiale :</strong> {selectedCandidate.job_filiale}</p>
                <p><strong>Étape :</strong> {selectedCandidate.stage}</p>

                {selectedCandidate.extra_doc_link && (
                  <p><strong>Portfolio :</strong> <a href={selectedCandidate.extra_doc_link.startsWith('http') ? selectedCandidate.extra_doc_link : `https://${selectedCandidate.extra_doc_link}`} target="_blank" rel="noopener noreferrer">Voir</a></p>
                )}

                {selectedCandidate.cv_file && (
                  <p>
                    <strong>CV :</strong><br />
                    <a
                      href={`${process.env.REACT_APP_DOMAINE}/uploads/${encodeURIComponent(selectedCandidate.cv_file)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="me-2"
                    >
                      📂 Ouvrir
                    </a>
                    <a
                      href={`${process.env.REACT_APP_DOMAINE}/api/candidates/${selectedCandidate.id}/download/cv`}
                      className="text-success"
                    >
                      ⬇️ Télécharger
                    </a>
                  </p>
                )}

                {selectedCandidate.cover_letter_file && (
                  <p>
                    <strong>Lettre de motivation :</strong><br />
                    <a
                      href={`${process.env.REACT_APP_DOMAINE}/uploads/${encodeURIComponent(selectedCandidate.cover_letter_file)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="me-2"
                    >
                      📂 Ouvrir
                    </a>
                    <a
                      href={`${process.env.REACT_APP_DOMAINE}/api/candidates/${selectedCandidate.id}/download/letter`}
                      className="text-success"
                    >
                      ⬇️ Télécharger
                    </a>
                  </p>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-success me-2"
                  onClick={() => handleAcceptCandidate(selectedCandidate.id)}
                >
                  ✓ Accepter
                </button>
                <button
                  className="btn btn-danger me-2"
                  onClick={() => handleRejectCandidate(selectedCandidate.id)}
                >
                  ✗ Refuser
                </button>
                <button className="btn btn-secondary" onClick={() => setSelectedCandidate(null)}>
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 📧 Email Modal */}
      {emailModal.show && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Email de notification - {emailModal.newStage}</h5>
                <button type="button" className="btn-close" onClick={cancelStageChange}></button>
              </div>
              <div className="modal-body">
                <p><strong>Destinataire :</strong> {emailModal.email}</p>
                <label className="form-label">Contenu de l'email :</label>
                <textarea
                  className="form-control"
                  rows="10"
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                ></textarea>
              </div>
              <div className="modal-footer">
                <button className="btn btn-success" onClick={confirmStageChange}>
                  Envoyer
                </button>
                <button className="btn btn-secondary" onClick={cancelStageChange}>
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CandidatesList;
